import { ApptEmailLog } from "@/components/ApptEmailLog";
import { useEffect, useState } from "react";
import { getLeadAppointments, createAppointment, updateAppointment, getAdvisors } from "@/lib/apiClient";
import { CalendarPlus, CalendarDays, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SlotPicker } from "@/components/SlotPicker";
import { RescheduleButton } from "@/components/RescheduleDialog";

export const APPT_TYPES = [
  { id: "adviesgesprek", label: "Adviesgesprek" }, { id: "bezichtiging", label: "Bezichtiging" },
  { id: "waardebepaling", label: "Waardebepaling" }, { id: "terugbelafspraak", label: "Terugbelafspraak" },
  { id: "overig", label: "Overig" },
];
export const APPT_MODES = [{ id: "telefonisch", label: "Telefonisch" }, { id: "zoom", label: "Videogesprek" }, { id: "op_locatie", label: "Op locatie" }];
export const modeLabel = (m) => (APPT_MODES.find((x) => x.id === m) || {}).label || "";
export const APPT_STATUS = { gepland: "Gepland", afgerond: "Afgerond", geannuleerd: "Geannuleerd", no_show: "Niet verschenen" };
export const typeLabel = (t) => (APPT_TYPES.find((x) => x.id === t) || {}).label || t;
export const fmtAppt = (s) => new Date(s).toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function AdminOverride({ value, onChange }) {
  return (
    <div className="rounded-lg border border-[#FBF0DC] bg-[#FFFAF0] p-2 space-y-2">
      <label className="flex items-center gap-2 text-xs text-[#B7791F] font-medium">
        <input type="checkbox" data-testid="appt-override" checked={value.on} onChange={(e) => onChange({ ...value, on: e.target.checked })} className="accent-[#B7791F]" />
        <AlertTriangle className="h-3.5 w-3.5" /> Admin: buiten beschikbaarheid plannen
      </label>
      {value.on && (
        <div className="grid grid-cols-2 gap-2">
          <input type="date" data-testid="appt-override-date" value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} className="fe-input" />
          <input type="time" data-testid="appt-override-time" value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} className="fe-input" />
        </div>
      )}
    </div>
  );
}

export function AppointmentPanel({ lead }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [advisor, setAdvisor] = useState("");
  const [slot, setSlot] = useState("");
  const [type, setType] = useState("adviesgesprek");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("telefonisch");
  const [contact, setContact] = useState({ contact_name: lead.name || "", contact_phone: lead.phone || "", contact_email: lead.email || "", location: lead.location || "" });
  useEffect(() => { setContact({ contact_name: lead.name || "", contact_phone: lead.phone || "", contact_email: lead.email || "", location: lead.location || "" }); }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setC = (k, v) => setContact((c) => ({ ...c, [k]: v }));
  const [ov, setOv] = useState({ on: false, date: "", time: "10:00" });
  const [refresh, setRefresh] = useState(0);
  const load = () => getLeadAppointments(lead.id).then(setList).catch(() => {});
  useEffect(() => { load(); }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getAdvisors().then((a) => { setAdvisors(a); if (a.length === 1) setAdvisor(a[0].id); }).catch(() => {}); }, []);

  const save = async () => {
    if (!advisor) return toast.error("Kies een adviseur");
    const start = ov.on ? `${ov.date}T${ov.time}` : slot;
    if (!start || start.startsWith("T")) return toast.error("Kies een tijdslot");
    try {
      if (mode === "op_locatie" && !contact.location.trim()) return toast.error("Vul de locatie in");
      await createAppointment({ lead_id: lead.id, start, type, mode, note, user_id: advisor, override: ov.on, ...contact, location: mode === "op_locatie" ? contact.location : "" });
      toast.success(contact.contact_email ? "Afspraak ingepland — bevestiging wordt gemaild naar klant en adviseur" : "Afspraak ingepland — bevestiging wordt gemaild naar de adviseur (klant heeft geen e-mailadres)"); setSlot(""); setNote(""); setOv({ on: false, date: "", time: "10:00" }); setRefresh((n) => n + 1); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Inplannen mislukt"); setRefresh((n) => n + 1); }
  };
  const setStatus = async (a, status) => {
    try { await updateAppointment(a.id, { status }); load(); setRefresh((n) => n + 1); } catch (e) { toast.error(e?.response?.data?.detail || "Wijzigen mislukt"); }
  };

  return (
    <div className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4 space-y-3" data-testid="appointment-panel">
      <div className="flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-[#16BFBF]" /><span className="text-sm font-semibold text-[#001C51]">Afspraak inplannen</span></div>
      {advisors.length === 0 ? <p className="text-xs text-[#B0413E]" data-testid="appt-no-advisors">Er zijn nog geen adviseurs ingesteld. Een admin kan dit aanzetten bij Teams & gebruikers.</p> : (
        <select data-testid="appt-advisor" value={advisor} onChange={(e) => { setAdvisor(e.target.value); setSlot(""); }} className="fe-input">
          <option value="">Kies adviseur…</option>
          {advisors.map((a) => <option key={a.id} value={a.id}>{a.name}{a.team_name ? ` — ${a.team_name}` : ""}</option>)}
        </select>
      )}
      {!ov.on && <SlotPicker key={`${advisor}-${refresh}`} advisorId={advisor} value={slot} onPick={setSlot} />}
      {user?.role === "admin" && advisor && <AdminOverride value={ov} onChange={setOv} />}
      <div className="inline-flex w-full rounded-xl border border-[#E2E7EC] bg-white p-1">
        {APPT_MODES.map((m) => (
          <button key={m.id} data-testid={`appt-mode-${m.id}`} onClick={() => setMode(m.id)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${mode === m.id ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}>{m.label}</button>
        ))}
      </div>
      {mode === "zoom" && <p data-testid="appt-video-hint" className="text-xs text-[#5B6675]">De vaste videolink van de adviseur wordt automatisch in de bevestiging gezet.</p>}
      {mode === "op_locatie" && <input data-testid="appt-location" value={contact.location} onChange={(e) => setC("location", e.target.value)} className="fe-input" placeholder="Adres / locatie" />}
      <div className="grid grid-cols-1 gap-2">
        <input data-testid="appt-contact-name" value={contact.contact_name} onChange={(e) => setC("contact_name", e.target.value)} className="fe-input" placeholder="Naam klant" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="appt-contact-phone" value={contact.contact_phone} onChange={(e) => setC("contact_phone", e.target.value)} className="fe-input" placeholder="Telefoon" />
          <input data-testid="appt-contact-email" value={contact.contact_email} onChange={(e) => setC("contact_email", e.target.value)} className="fe-input" placeholder="E-mail" />
        </div>
      </div>
      <label className="text-xs text-[#5B6675]">Doel van de afspraak
        <select data-testid="appt-type" value={type} onChange={(e) => setType(e.target.value)} className="fe-input">{APPT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
      </label>
      <textarea data-testid="appt-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="fe-input resize-y" placeholder="Notitie (wat bespreken…)" />
      <button data-testid="appt-save" onClick={save} className="w-full py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]">
        {slot && !ov.on ? `Inplannen op ${fmtAppt(slot)}` : "Inplannen"}
      </button>
      <div className="space-y-2 pt-1" data-testid="lead-appointments">
        {list.length === 0 ? <p className="text-sm text-[#5B6675]">Nog geen afspraken met deze lead.</p> : list.map((a) => (
          <div key={a.id} data-testid={`lead-appt-${a.id}`} className={`rounded-xl border border-[#E2E7EC] bg-white p-3 ${a.status === "geannuleerd" ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-[#001C51]" />
              <span className="text-sm font-medium text-[#16202B]">{fmtAppt(a.start)}</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-[#E3E9F5] text-[#001C51]">{typeLabel(a.type)}</span>
            </div>
            <p className="text-xs text-[#5B6675] mt-1">Adviseur: {a.user_name} · {a.duration_min} min{a.mode ? ` · ${modeLabel(a.mode)}` : ""}{a.outside_availability ? " · buiten beschikbaarheid" : ""}</p>
            {a.location && <p className="text-xs text-[#5B6675] break-all">{a.mode === "zoom" ? "Videolink" : "Locatie"}: {a.location}</p>}
            <ApptEmailLog emails={a.emails} testid={`lead-appt-emails-${a.id}`} />
            {a.created_by && <p className="text-[11px] text-[#8994A3]">Geboekt door {a.created_by}</p>}
            {a.note && <p className="text-xs text-[#16202B] mt-1 whitespace-pre-wrap">{a.note}</p>}
            <div className="flex items-center gap-2 mt-2">
              <select data-testid={`lead-appt-status-${a.id}`} value={a.status} onChange={(e) => setStatus(a, e.target.value)} className="fe-input py-1.5 text-xs flex-1">
                {Object.entries(APPT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {a.status === "gepland" && <RescheduleButton appt={a} onDone={() => { load(); setRefresh((n) => n + 1); }} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
